import { getLowercaseCountryCodeFromIntlLocale, localeToIntlLocale } from "@core/i18n/locale-helper";
import { getLogger } from "@core/logging/logger";
import { AuthService, RegistrationUserAttributes } from "@domain/authentication/auth-service";
import { UsernameExistsCodeError } from "@domain/authentication/authentication-errors";
import { InfraManager } from "@domain/infra/infra-manager";
import { CreatePtInput, PtInvitation, ValidatePtInput } from "@domain/register/register";
import { RegisterService } from "@domain/register/register-service";
import { LocaleManager } from "@domain/localization/locale-manager";
import { isNil } from "@utils/assert";

export class RegisterManager {
	private readonly logger = getLogger("🖊️ [RegisterManager]");

	constructor(
		private readonly registerService: RegisterService,
		private readonly localeManager: LocaleManager,
		private readonly authService: AuthService,
		private readonly infraManager: InfraManager,
	) {}

	async fetchPtInvitation(invitationId: string): Promise<PtInvitation> {
		return await this.registerService.fetchPtInvitation(invitationId);
	}

	async getBestLocaleForCountry(countryCode: string): Promise<string> {
		const { locale } = await this.registerService.sendCountryCode(countryCode);
		return localeToIntlLocale(locale);
	}

	async sendCountryCode(countryCode: string): Promise<void> {
		await this.infraManager.changeInfrastructureUsingCountryCode(countryCode);
		const intlLocale = await this.getBestLocaleForCountry(countryCode);
		this.localeManager.changeLocale(intlLocale);
	}

	async changeLocale(intlLocale: string): Promise<void> {
		const countryCode = getLowercaseCountryCodeFromIntlLocale(intlLocale);
		await this.infraManager.changeInfrastructureUsingCountryCode(countryCode);
		this.localeManager.changeLocale(intlLocale);
	}

	async signUpWithEmail(email: string, password: string, userAttributes: RegistrationUserAttributes): Promise<void> {
		this.logger.debug("Try to sign up");
		const isEmailAvailable = await this.authService.isEmailAvailable(email);
		if (!isEmailAvailable) {
			this.logger.debug("Email already exists");
			throw new UsernameExistsCodeError(`Email ${email} already exists`);
		}
		await this.authService.signUpEmail(email, password, userAttributes);
	}

	async resendSignUpCode(email: string): Promise<void> {
		await this.authService.resendSignUpValidationCode(email);
	}

	async sendRegistrationData(data: CreatePtInput): Promise<void> {
		await this.registerService.sendRegistrationData(data);
	}

	async validateProfessionalInformation(data: ValidatePtInput): Promise<void> {
		await this.registerService.validateProfessionalInfo(data);
	}

	async confirmEmailAddress(email: string, code: string): Promise<void> {
		await this.authService.validateSignUpConfirmationCode(code, email);
	}

	async login(email: string, password: string): Promise<void> {
		await this.authService.loginEmail(email, password, Promise.resolve);
	}

	async retrieveUserAttributes(
		email: string,
		password: string,
		onSuccess?: (userAttributes: RegistrationUserAttributes) => void,
		onError?: (error: unknown) => void,
	): Promise<void> {
		if (!this.authService.isAuthenticated()) {
			await this.authService.loginEmail(email, password, Promise.resolve);
		}
		try {
			const userAttributes = this.authService.getRegistrationUserAttributes();
			if (isNil(userAttributes)) {
				onError?.(new Error("UserAttributes are undefined. The registration cannot continue"));
				return;
			}
			onSuccess?.(userAttributes);
		} catch (error) {
			console.error("Failed to retrieve user attributes", error);
			onError?.(error);
		}
	}
}
